import React, { useCallback, useEffect, useState } from 'react'
import { ImagePlus } from 'lucide-react'
import { apiDelete, apiFetch, apiGet, apiPatch, apiPost } from '../../api'
import { Alert, Button, DataTable, EmptyState, Loading, StatusPill, useConfirm } from '../../components/ui'
import { readError } from './shared'

const productInitials = (name) =>
  (name || '')
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?'

function ProductsTab({ tournament }) {
  const confirm = useConfirm()
  const [products, setProducts] = useState([])
  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ name: '', price: '', description: '', stock: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploadingImage, setUploadingImage] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [pRes, sRes] = await Promise.all([
        apiGet(`/api/tournaments/${tournament.id}/products`),
        apiGet(`/api/tournaments/${tournament.id}/sales`)
      ])
      if (pRes.ok) setProducts(await pRes.json())
      if (sRes.ok) setSales(await sRes.json())
    } finally {
      setLoading(false)
    }
  }, [tournament.id])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview)
    }
  }, [imagePreview])

  const uploadProductImage = async () => {
    if (!imageFile || !tournament.organizerFacilityId) return null
    setUploadingImage(true)
    try {
      const body = new FormData()
      body.append('file', imageFile)
      const res = await apiFetch(`/api/images/facility/${tournament.organizerFacilityId}/product-image`, {
        method: 'POST',
        body
      })
      if (!res.ok) {
        setError(await readError(res, 'Kunne ikke laste opp bilde'))
        return null
      }
      const data = await res.json()
      return data.url || null
    } finally {
      setUploadingImage(false)
    }
  }

  const createProduct = async (e) => {
    e.preventDefault()
    setError('')
    if (!form.name.trim() || !form.price) {
      setError('Navn og pris er påkrevd.')
      return
    }
    setSaving(true)
    try {
      const imageUrl = imageFile ? await uploadProductImage() : null
      if (imageFile && !imageUrl) return
      const res = await apiPost(`/api/tournaments/${tournament.id}/products`, {
        name: form.name.trim(),
        price: Number(form.price),
        description: form.description.trim() || null,
        stock: form.stock ? Number(form.stock) : null,
        imageUrl
      })
      if (res.ok) {
        setForm({ name: '', price: '', description: '', stock: '' })
        setImageFile(null)
        if (imagePreview) URL.revokeObjectURL(imagePreview)
        setImagePreview(null)
        await load()
      } else {
        setError(await readError(res, 'Kunne ikke opprette produkt (er Stripe satt opp?)'))
      }
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (p) => {
    await apiPatch(`/api/products/${p.id}`, { isActive: !p.isActive })
    load()
  }

  const removeProduct = async (p) => {
    if (!(await confirm({ message: `Slette «${p.name}»?`, confirmLabel: 'Slett', danger: true }))) return
    await apiDelete(`/api/products/${p.id}`)
    load()
  }

  const paidSales = sales.filter((s) => s.status === 'paid')
  const revenue = paidSales.reduce((sum, s) => sum + Number(s.amount || 0), 0)
  const missingRequired = error === 'Navn og pris er påkrevd.'

  const productColumns = [
    {
      key: 'name',
      header: 'Produkt',
      render: (p) => (
        <span className="tn-product-cell">
          {p.imageUrl ? (
            <img src={p.imageUrl} alt="" className="tn-product-thumb" />
          ) : (
            <span className="tn-product-thumb placeholder" aria-hidden="true">{productInitials(p.name)}</span>
          )}
          <span className="cell-title">{p.name}</span>
        </span>
      )
    },
    { key: 'price', header: 'Pris', align: 'right', className: 'cell-mono', render: (p) => `${p.price} ${p.currency}` },
    { key: 'sold', header: 'Solgt', align: 'right', className: 'cell-mono', render: (p) => p.soldCount },
    {
      key: 'stock',
      header: 'Lager',
      align: 'right',
      className: 'cell-mono',
      render: (p) => (p.stock == null ? 'Ubegrenset' : p.remaining)
    },
    {
      key: 'status',
      header: 'Status',
      render: (p) => <StatusPill tone={p.isActive ? 'open' : 'muted'}>{p.isActive ? 'Aktiv' : 'Skjult'}</StatusPill>
    },
    {
      key: 'actions',
      header: 'Handling',
      align: 'right',
      className: 'actions',
      render: (p) => (
        <span className="tn-row-actions">
          <Button variant="tertiary" size="xs" onClick={() => toggleActive(p)}>
            {p.isActive ? 'Skjul' : 'Vis'}
          </Button>
          <Button variant="danger-soft" size="xs" aria-label={`Slett ${p.name}`} onClick={() => removeProduct(p)}>
            Slett
          </Button>
        </span>
      )
    }
  ]

  const salesColumns = [
    { key: 'productName', header: 'Produkt', className: 'tn-strong' },
    { key: 'buyerName', header: 'Kjøper' },
    { key: 'amount', header: 'Beløp', align: 'right', className: 'cell-mono', render: (s) => `${s.amount} ${s.currency}` },
    {
      key: 'redeemed',
      header: 'Innløst',
      render: (s) => (
        <span className={`tn-redeemed ${s.redeemed ? 'yes' : 'no'}`}>
          <span className="tn-dot" aria-hidden="true" />
          {s.redeemed ? 'Ja' : 'Nei'}
        </span>
      )
    },
    {
      key: 'createdAt',
      header: 'Dato',
      className: 'cell-mono muted',
      render: (s) => (s.createdAt ? new Date(s.createdAt).toLocaleDateString('nb-NO') : '—')
    }
  ]

  return (
    <div className="stack-lg">
      <section className="card tn-card" aria-labelledby="tn-np-h">
        <div className="stack-sm">
          <h2 id="tn-np-h" className="section-title">Nytt produkt</h2>
          <span className="section-sub">Selges i appen på turneringssiden. Kjøperen får en kode som innløses under Salg.</span>
        </div>
        <form onSubmit={createProduct} className="tn-product-form" noValidate>
          <label className="field tn-pf-name">
            <span className="field-label">Navn</span>
            <input
              value={form.name}
              aria-invalid={missingRequired && !form.name.trim() ? true : undefined}
              className={missingRequired && !form.name.trim() ? 'tn-invalid' : undefined}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label className="field tn-pf-num">
            <span className="field-label">Pris (kr)</span>
            <input
              type="number"
              min={0}
              className={`input-mono ${missingRequired && !form.price ? 'tn-invalid' : ''}`}
              aria-invalid={missingRequired && !form.price ? true : undefined}
              placeholder="0"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
            />
          </label>
          <label className="field tn-pf-num">
            <span className="field-label">Lager</span>
            <input
              type="number"
              min={0}
              className="input-mono"
              value={form.stock}
              onChange={(e) => setForm({ ...form, stock: e.target.value })}
              placeholder="Ubegrenset"
            />
          </label>
          <label className="field span-all">
            <span className="field-label">Beskrivelse</span>
            <textarea
              rows={2}
              placeholder="Hva får kjøperen?"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <div className="tn-pf-image span-all">
            {imagePreview ? (
              <img src={imagePreview} alt="Forhåndsvisning av produktbilde" className="tn-image-preview" />
            ) : (
              <span className="tn-image-preview placeholder" aria-hidden="true">
                <ImagePlus size={20} strokeWidth={1.9} />
              </span>
            )}
            <label className="field tn-file-field">
              <span className="field-label">Bilde (valgfritt)</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null
                  setImageFile(file)
                  if (imagePreview) URL.revokeObjectURL(imagePreview)
                  setImagePreview(file ? URL.createObjectURL(file) : null)
                }}
              />
            </label>
            <Button type="submit" className="tn-pf-submit" disabled={saving || uploadingImage}>
              {saving || uploadingImage ? 'Lagrer…' : 'Legg til produkt'}
            </Button>
          </div>
        </form>
        {error && <Alert tone="error">{error}</Alert>}
      </section>

      <section className="stack" aria-labelledby="tn-pr-h">
        <h2 id="tn-pr-h" className="section-title">Produkter</h2>
        {loading ? (
          <Loading>Laster…</Loading>
        ) : products.length === 0 ? (
          <EmptyState compact title="Ingen produkter ennå." />
        ) : (
          <DataTable columns={productColumns} rows={products} minWidth={760} caption="Produkter" />
        )}
      </section>

      <section className="stack" aria-labelledby="tn-sa-h">
        <h2 id="tn-sa-h" className="section-title">
          Salg {paidSales.length > 0 && <>· <span className="tn-mono-heading">{revenue.toLocaleString('nb-NO')} kr</span></>}
        </h2>
        {sales.length === 0 ? (
          <EmptyState compact title="Ingen salg ennå." />
        ) : (
          <DataTable
            columns={salesColumns}
            rows={sales}
            minWidth={680}
            caption="Salg"
            footer={paidSales.length > 0 ? (
              <tr>
                <td colSpan={2}>Sum betalt ({paidSales.length})</td>
                <td className="num cell-mono">{revenue.toLocaleString('nb-NO')} kr</td>
                <td colSpan={2} />
              </tr>
            ) : null}
          />
        )}
      </section>
    </div>
  )
}

export default ProductsTab
